import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useModalContext } from '@frontend/dynamic-wrapper';
import { useYouCancelProcess } from '../YouCancelProcessContext/YouCancelProcessContext';
import { useYouProcessTheme } from '../YouProcessThemeContext/YouProcessThemeContext';
import { useYouHeader } from '../YouHeaderContext/YouHeaderContext';
import { YouLogo } from '../YouLogo/YouLogo';
import { YouCloseIcon } from '../YouCloseIcon/YouCloseIcon';
import { YouSuccessIcon } from '../YouSuccessIcon/YouSuccessIcon';
import { YouSpinner } from '../YouSpinner/YouSpinner';

const HeaderActionsArea = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: auto;
  height: 60px;
  box-sizing: border-box;
  padding: 16px;

  img {
    cursor: pointer;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -8px;
    height: 8px;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.10), transparent);
    opacity: var(--scroll-shadow-top, 0);
    transition: opacity 0.2s ease;
    pointer-events: none;
  }
`;

const ActionArea = styled.div`
  display: flex;
  min-width: 24px;
  align-items: center;
  gap: 4px;

  button {
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
  }
`;

const RightActionArea = styled(ActionArea)`
  position: relative;
  justify-content: flex-end;
  min-height: 24px;
`;

const StatusSlot = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  pointer-events: none;
  white-space: nowrap;
`;

const FADE_DURATION_MS = 300;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  animation: ${({ $isExiting }) => ($isExiting ? fadeOut : fadeIn)}
    ${FADE_DURATION_MS}ms ease-in forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const FadingText = styled.span`
  animation: ${fadeIn} ${FADE_DURATION_MS}ms ease-in forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const YouHeaderActions = () => {
  const modalContext = useModalContext();
  const { cancelModal } = useYouCancelProcess() ?? {};
  const theme = useYouProcessTheme();
  const {
    isLoading,
    loadingDescription,
    isSuccess,
    successMessage,
    showCloseButton,
    showBackButton,
    showLogoButton,
    onClose,
    onBack,
    onLogo,
    children,
    logo,
    logoSrc,
    logoAlt,
    closeIcon,
    closeSrc,
    closeAlt,
    backIcon,
    backSrc,
    backAlt,
  } = useYouHeader();

  const isModalOpen = (modalContext?.modalStack?.length ?? 0) > 0;

  let desiredStatus = null;
  if (isLoading) {
    desiredStatus = 'loading';
  } else if (isSuccess) {
    desiredStatus = 'success';
  }
  const [activeStatus, setActiveStatus] = useState(desiredStatus);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (desiredStatus) {
      setActiveStatus(desiredStatus);
      setIsExiting(false);
      return undefined;
    }
    if (!activeStatus) return undefined;
    setIsExiting(true);
    const timeout = setTimeout(() => {
      setActiveStatus(null);
      setIsExiting(false);
    }, FADE_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [desiredStatus, activeStatus]);

  const handleCloseClick = () => {
    onClose?.();
    if (cancelModal && modalContext?.openModal) {
      modalContext.openModal(cancelModal);
    }
  };

  const defaultLogo = <YouLogo color={theme?.primaryColor} title="logo" />;
  const logoNode =
    logo || (logoSrc ? <img src={logoSrc} alt={logoAlt} /> : defaultLogo);

  const defaultCloseIcon = <YouCloseIcon />;
  const closeIconNode =
    closeIcon ||
    (closeSrc ? <img src={closeSrc} alt={closeAlt} /> : defaultCloseIcon);

  const backIconNode =
    backIcon || (backSrc ? <img src={backSrc} alt={backAlt} /> : null);

  const renderLeft = () => {
    if (showBackButton && onBack) {
      return (
        <button
          type="button"
          onClick={onBack}
          data-testid="back-button"
          aria-label={backAlt}
        >
          {backIconNode}
        </button>
      );
    }
    if (showLogoButton && onLogo) {
      return (
        <button type="button" onClick={onLogo} data-testid="logo-button">
          {logoNode}
        </button>
      );
    }
    return logoNode;
  };

  const renderStatus = () => {
    if (activeStatus === 'loading') {
      return (
        <StatusSlot>
          <StatusIndicator
            data-testid="header-loading-indicator"
            $isExiting={isExiting}
          >
            {loadingDescription && (
              <FadingText
                key={loadingDescription}
                data-testid="header-loading-text"
              >
                {loadingDescription}
              </FadingText>
            )}
            <YouSpinner size={24} data-testid="header-loading-spinner" />
          </StatusIndicator>
        </StatusSlot>
      );
    }
    if (activeStatus === 'success') {
      return (
        <StatusSlot>
          <StatusIndicator
            data-testid="header-success-indicator"
            $isExiting={isExiting}
          >
            {successMessage && (
              <FadingText
                key={successMessage}
                data-testid="header-success-text"
              >
                {successMessage}
              </FadingText>
            )}
            <YouSuccessIcon size={24} alt={successMessage || ''} />
          </StatusIndicator>
        </StatusSlot>
      );
    }
    return null;
  };

  const renderCloseButton = () => {
    if (activeStatus) return null;
    if (isModalOpen) return null;
    if (!showCloseButton) return null;
    if (!onClose && !cancelModal) return null;
    return (
      <button
        type="button"
        onClick={handleCloseClick}
        data-testid="close-button"
        aria-label={closeAlt}
      >
        {closeIconNode}
      </button>
    );
  };

  return (
    <HeaderActionsArea data-testid="header-actions">
      <ActionArea>{renderLeft()}</ActionArea>
      <ActionArea>{children}</ActionArea>
      <RightActionArea>
        {renderCloseButton()}
        {renderStatus()}
      </RightActionArea>
    </HeaderActionsArea>
  );
};

export { YouHeaderActions };
